// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { buildParameterSerializer, CartItem } from "@coveo/headless-react/ssr-commerce";
import { CoveoNavigatorContext } from "@NA/Components/Coveo/Contexts/CoveoNavigatorContext";
import { CustomHandler } from "@NA/Store/CustomHandlerCreator/CustomHandlerCreator";
import { CoveoActions } from "@NA/Store/Reducer/CoveoCommerceReducer";
import { getPath, getSearchParams, isPlp, isSearch } from "@NA/Utils/Coveo/CoveoUtils";
import { createHandlerChainRunner, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = HasOnSuccess;

type HandlerType = CustomHandler<Parameter>;

const DispatchBeginLoadCoveoStaticState: HandlerType = ({ dispatch }) => {
    dispatch({
        type: CoveoActions.BEGIN_LOAD_STATIC_STATE,
    });
};

const DispatchSetCoveoProviderDefinition: HandlerType = async ({ dispatch, getState }) => {
    const state = getState();

    const search = isSearch(state);
    const plp = isPlp(state);

    const providerDefinition = search
        ? state.ADIGlobalData.coveoCommerce.engineDefinition.searchEngineDefinition
        : plp
        ? state.ADIGlobalData.coveoCommerce.engineDefinition.listingEngineDefinition
        : state.ADIGlobalData.coveoCommerce.engineDefinition.standaloneEngineDefinition;

    if (!providerDefinition) return;

    const navigatorContext = new CoveoNavigatorContext();
    providerDefinition.setNavigatorContextProvider(() => navigatorContext);

    dispatch({
        type: CoveoActions.SET_PROVIDER_DEFINITION,
        providerDefinition,
        navigatorContext,
    });
};

export const DispatchSetCoveoCartItems: HandlerType = ({ dispatch, getState }) => {
    const state = getState();
    const token = state.ADIGlobalData.coveoCommerce.token;
    const isAuthenticated = state.context.session?.isAuthenticated;
    if (IS_SERVER_SIDE || !token || !isAuthenticated) {
        return;
    }

    const cartLines = getCurrentCartState(state).value?.cartLines;
    const cartItems: CartItem[] | null = cartLines
        ? cartLines
              .map((line: CartLineModel) => {
                  if (!line.productId || !line.productName || !line.pricing?.actualPrice || !line.qtyOrdered) {
                      return null;
                  }
                  return {
                      productId: line.productId,
                      name: line.productName,
                      price: line.pricing?.actualPrice,
                      quantity: line.qtyOrdered,
                  };
              })
              .filter((item: CartItem | null) => item !== null)
        : null;

    dispatch({
        type: CoveoActions.SET_CART_ITEMS,
        cartItems,
    });
};

const DispatchLoadCoveoStaticState: HandlerType = async ({ dispatch, getState }) => {
    const state = getState();
    const token = state.ADIGlobalData.coveoCommerce.token;
    const definition = state.ADIGlobalData.coveoCommerce.providerDefinition;
    const cartItems = state.ADIGlobalData.coveoCommerce.cartItems ?? [];

    if (token && definition) {
        const url = `${token.commerceView}${getPath(state).toLowerCase()}`;

        const { deserialize } = buildParameterSerializer();
        const params = getSearchParams(state);
        const urlParams = new URLSearchParams(params);
        const parameters = deserialize(urlParams);

        const staticState = await definition.fetchStaticState({
            controllers: {
                cart: { initialState: { cartItems } },
                context: {
                    language: token.language.split("-")[0],
                    country: token.country,
                    currency: token.currency,
                    view: { url },
                },
                parameterManager: { initialState: { parameters } },
            },
        });

        dispatch({
            type: CoveoActions.LOAD_STATIC_STATE,
            staticState,
        });
    }
};

const DispatchCompleteLoadCoveoStaticState: HandlerType = ({ dispatch }) => {
    dispatch({
        type: CoveoActions.COMPLETE_LOAD_STATIC_STATE,
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    props.parameter.onSuccess?.();
};

export const chain = [
    DispatchBeginLoadCoveoStaticState,
    DispatchSetCoveoProviderDefinition,
    DispatchSetCoveoCartItems,
    DispatchLoadCoveoStaticState,
    DispatchCompleteLoadCoveoStaticState,
    ExecuteOnSuccessCallback,
];

const loadCoveoStaticState = createHandlerChainRunner(chain, "LoadCoveoStaticState");
export default loadCoveoStaticState;
